/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2024 - Finnish Meteorological Institute (FMI)
 * Copyright 2024 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, Typography } from '@mui/material';
import type { ReactElement } from 'react';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import {
  ReactHookFormHiddenInput,
  ReactHookFormTextField,
} from '@opengeoweb/form-fields';
import {
  AlertBanner,
  ConfirmationButtons,
  SystemScope,
} from '@opengeoweb/shared';
import { Exclamation } from '@opengeoweb/theme';
import { WorkspacePresetAction } from '../../store/workspace/types';
import type {
  WorkspaceListError,
  WorkspacePresetFormValues,
} from '../../store/workspaceList/types';
import { WorkspaceListErrorType } from '../../store/workspaceList/types';
import { useWorkspaceTranslation } from '../../utils/i18n';
import PresetLabelsPanelWrapper from '../LabelsPanel/PresetsLabelsPanelWrapper';

export const ERROR_DUPLICATE_TITLE_BE =
  'Preset title already in use, please choose a different title';
export const ERROR_LONG_TITLE_BE = 'Preset title cannot exceed 100 characters';

const validateTitle = (value: string): boolean | string => {
  if (!value) {
    return true;
  }
  return !value.trim().length ? 'This field is required' : true;
};

export const emptyWorkspacePreset = {
  scope: SystemScope.user,
  title: '',
  abstract: '',
};

const SHORT_DESC_LIMIT = 100;
const VIEW_TITLE_LIMIT = 100;

interface WorkspacePresetsFormProps {
  error?: WorkspaceListError;
  onSubmit?: () => void;
  action: WorkspacePresetAction;
  formValues: WorkspacePresetFormValues;
  isLoading?: boolean;
  hasSavedWorkspace?: boolean;
  showInlineActions?: boolean;
  confirmLabel?: string;
  cancelLabel?: string;
  onClose?: () => void;
  dialogTitle?: string;
}

const WorkspacePresetsForm: React.FC<WorkspacePresetsFormProps> = ({
  error,
  onSubmit,
  action,
  formValues,
  isLoading = false,
  hasSavedWorkspace = false,
  showInlineActions = false,
  confirmLabel,
  cancelLabel,
  onClose,
  dialogTitle,
}: WorkspacePresetsFormProps) => {
  const { t } = useWorkspaceTranslation();
  const { setError, watch } = useFormContext();

  const titleField = 'title';
  const { title, description, abstract, ...otherFormValues } = {
    ...emptyWorkspacePreset,
    ...formValues,
  };
  const { views = [] } = formValues;
  const isViewsOnly = action === WorkspacePresetAction.EDIT_TITLE;

  const descriptionValue = watch('description') ?? '';
  const remaining = Math.max(0, SHORT_DESC_LIMIT - descriptionValue.length);

  const hiddenFields = Object.keys(otherFormValues).map((name) => ({
    name,
    value: otherFormValues[name as keyof typeof otherFormValues],
  }));

  const shouldShowTitleError =
    error?.type === WorkspaceListErrorType.VERBATIM_ERROR &&
    (error.message === ERROR_DUPLICATE_TITLE_BE ||
      error.message === ERROR_LONG_TITLE_BE);

  React.useEffect(() => {
    // autoFocus
    const field = document.querySelector(
      `[name="${titleField}"]`,
    ) as HTMLInputElement;
    field?.focus();
  }, []);

  React.useEffect(() => {
    // set title error on title field
    if (shouldShowTitleError) {
      setError(titleField, { message: error.message });
    }
  }, [error, setError, shouldShowTitleError]);

  const onSubmitForm = (event: React.FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    onSubmit!();
  };

  const hasFormInput =
    action === WorkspacePresetAction.DUPLICATE ||
    action === WorkspacePresetAction.SAVE ||
    action === WorkspacePresetAction.SAVE_AS ||
    action === WorkspacePresetAction.EDIT ||
    action === WorkspacePresetAction.EDIT_TITLE ||
    action === WorkspacePresetAction.SAVE_SYSTEM_PRESET ||
    action === WorkspacePresetAction.SAVE_SYSTEM_PRESET_AS;

  // disable form inputs
  const isDisabled =
    isLoading ||
    (hasSavedWorkspace && action !== WorkspacePresetAction.EDIT_TITLE);
  const shouldShowLabelsForm =
    action === WorkspacePresetAction.SAVE_SYSTEM_PRESET_AS ||
    action === WorkspacePresetAction.SAVE_AS ||
    action === WorkspacePresetAction.EDIT;

  return (
    <>
      {!!error && !shouldShowTitleError && (
        <Box sx={{ marginBottom: 2 }}>
          <AlertBanner
            title={
              error.type === WorkspaceListErrorType.TRANSLATABLE_ERROR
                ? t(error.key, error.params)
                : error.message
            }
          />
        </Box>
      )}

      {!!hasFormInput && (
        <form
          data-testid="workspace-preset-form"
          onSubmit={onSubmitForm}
          style={{ height: '100%' }}
        >
          {isViewsOnly ? (
            <>
              {/* Views-only: keep title/abstract as hidden inputs */}
              <ReactHookFormHiddenInput defaultValue={title} name="title" />
              <ReactHookFormHiddenInput
                defaultValue={abstract}
                name="abstract"
              />

              {action === WorkspacePresetAction.EDIT_TITLE && (
                <Box sx={{ mt: 3 }}>
                  {((): ReactElement => {
                    const thisView = views?.[0] ?? {};
                    const mosaicNodeId = thisView.mosaicNodeId ?? '';

                    return (
                      <Box sx={{ mb: 1.5 }}>
                        <ReactHookFormHiddenInput
                          defaultValue={mosaicNodeId}
                          name="views.0.mosaicNodeId"
                        />

                        {/* Only editable field: the view title */}
                        <ReactHookFormTextField
                          defaultValue={thisView.title ?? ''}
                          disabled={isDisabled}
                          label={mosaicNodeId || t('workspace-view-title')}
                          name="views.0.title"
                          rules={{
                            required: false,
                            maxLength: {
                              value: VIEW_TITLE_LIMIT,
                              message: `Maximum ${VIEW_TITLE_LIMIT} characters`,
                            },
                          }}
                          slotProps={{
                            htmlInput: { maxLength: VIEW_TITLE_LIMIT },
                          }}
                        />
                      </Box>
                    );
                  })()}
                </Box>
              )}

              {hiddenFields.map(({ name, value }) => (
                <ReactHookFormHiddenInput
                  data-testid={name}
                  defaultValue={value}
                  key={name}
                  name={name}
                />
              ))}
            </>
          ) : (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  minHeight: 360,
                  overflow: 'hidden',
                  display: 'grid',
                  gridTemplateColumns: shouldShowLabelsForm
                    ? { xs: '1fr', md: '300px 1fr' }
                    : '1fr',
                  alignItems: 'stretch',
                }}
              >
                {!!shouldShowLabelsForm && (
                  <Box
                    sx={{
                      pr: { md: 2 },
                      minWidth: 288,
                      height: '100%',
                      overflowY: 'auto',
                      overflowX: 'hidden',
                      bgcolor: 'geowebColors.background.surface',
                      maxWidth: '80%',
                    }}
                  >
                    <PresetLabelsPanelWrapper />
                  </Box>
                )}

                <Box
                  sx={{
                    pl: { md: 2 },
                    pr: 2,
                    py: 2,
                    minWidth: 0,
                    overflow: 'hidden',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {!!dialogTitle && !!shouldShowLabelsForm && (
                    <Typography
                      sx={{ mb: 2, fontWeight: 500 }}
                      variant="subtitle1"
                    >
                      {dialogTitle}
                    </Typography>
                  )}

                  <Box sx={{ mb: 2 }}>
                    <ReactHookFormTextField
                      defaultValue={title}
                      disabled={isDisabled}
                      error={shouldShowTitleError}
                      fullWidth
                      label={t('workspace-dialog-name')}
                      name={titleField}
                      rules={{ required: true, validate: { validateTitle } }}
                    />
                  </Box>

                  <Box sx={{ mb: 2 }}>
                    <ReactHookFormTextField
                      defaultValue={description}
                      disabled={isDisabled}
                      fullWidth
                      label={t('workspace-dialog-description')}
                      multiline
                      name="description"
                      rows={3}
                      rules={{ required: false, maxLength: SHORT_DESC_LIMIT }}
                    />
                    <Typography
                      component="div"
                      marginBottom={2}
                      marginLeft={1}
                      marginTop={1}
                      variant="caption"
                    >
                      <Typography
                        color="textSecondary"
                        component="span"
                        variant="inherit"
                      >
                        {t('workspace-char-limit')}{' '}
                      </Typography>
                      <Typography
                        color={remaining === 0 ? 'error' : 'primary'}
                        component="span"
                        variant="inherit"
                      >
                        {remaining}
                      </Typography>
                    </Typography>

                    <ReactHookFormTextField
                      defaultValue={abstract}
                      disabled={isDisabled}
                      fullWidth
                      label={t('workspace-dialog-abstract')}
                      multiline
                      name="abstract"
                      rows={6}
                      rules={{ required: false }}
                    />
                  </Box>

                  {!!showInlineActions && (
                    <Box
                      sx={{
                        mt: 'auto',
                        display: 'flex',
                        justifyContent: 'flex-end',
                      }}
                    >
                      <ConfirmationButtons
                        cancelLabel={cancelLabel ?? ''}
                        confirmLabel={confirmLabel ?? ''}
                        isLoading={isLoading}
                        onClose={onClose!}
                        onSubmit={onSubmit!}
                      />
                    </Box>
                  )}
                </Box>
              </Box>

              {hiddenFields.map(({ name, value }) => (
                <ReactHookFormHiddenInput
                  data-testid={name}
                  defaultValue={value}
                  key={name}
                  name={name}
                />
              ))}
            </Box>
          )}
        </form>
      )}

      {(action === WorkspacePresetAction.DELETE ||
        action === WorkspacePresetAction.DELETE_SYSTEM_PRESET) && (
        <>
          <Typography variant="body1">
            {t('workspace-dialog-confirm-delete-message', { TITLE: title })}
          </Typography>
          <ReactHookFormHiddenInput
            defaultValue={title}
            key="title"
            name="title"
          />
        </>
      )}

      {action === WorkspacePresetAction.RESET && (
        <Box>
          <Typography variant="body1">
            <Exclamation
              sx={{
                top: '6px',
                position: 'relative',
                color: 'geowebColors.functional.warning',
              }}
            />
            {formValues.scope === 'system'
              ? t('workspace-dialog-confirm-reset-message')
              : t('workspace-dialog-confirm-reset-personal-message')}
          </Typography>
          <ReactHookFormHiddenInput
            defaultValue={title}
            key="title"
            name="title"
          />
        </Box>
      )}
    </>
  );
};

export default WorkspacePresetsForm;
